import { useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import { MoreHorizontal, RotateCcw } from "lucide-react";
import { DeleteBtn } from "@/components/Delete";
import { TypeTableProps } from "@/App/Plans/Table";
import { cn } from "@/lib/utils";
import { colors } from "@/utils/colors";
import { TableDataProps } from "@/pages/TimeEntryMirror";
import { deleteTimeEntryMirror, reprocessTimeEntryMirror } from "@/Api/timeEntryMirror";
import { usePermissions } from "@/hooks/usePermissions";
import { If } from "@/components/If";
import { ShowBtn } from "@/App/TimeEntryMirror/Show";
import { Button } from "@/components/ui/button";

const STATUS_LABELS: Record<number, string> = {
  1: "Pendente",
  2: "Processando",
  3: "Concluído",
  4: "Concluído com erros",
  5: "Falhou",
};

const getStatusColor = (statusId: number) => {
  switch (statusId) {
    case 1:
      return "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400";
    case 2:
      return "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400";
    case 3:
      return "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400";
    case 4:
      return "bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400";
    case 5:
      return "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400";
    default:
      return "bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400";
  }
};

const formatDate = (date: string) =>
  new Date(date).toLocaleDateString("pt-BR", { timeZone: "UTC" });

export const TableData = ({
  dataApi,
  pageActions,
  updateFront,
}: TypeTableProps<TableDataProps>) => {
  const { hasPermission } = usePermissions();
  const [reprocessingUuid, setReprocessingUuid] = useState<string | null>(null);

  const handleReprocess = async (uuid: string) => {
    setReprocessingUuid(uuid);
    const { data, ok } = await reprocessTimeEntryMirror(uuid);
    setReprocessingUuid(null);
    if (!ok) return;

    updateFront((prev) => ({
      ...prev,
      data: prev.data.map((item) => (item.uuid === uuid ? { ...item, ...data.response } : item)),
    }));
  };

  return (
    <>
      <Table className="bg-zinc-50 rounded dark:bg-[#2c2c2c]">
        <TableHeader>
          <TableRow>
            <TableHead>Unidade de negócio</TableHead>
            <TableHead>Colaborador</TableHead>
            <TableHead>Data início</TableHead>
            <TableHead>Data fim</TableHead>
            <TableHead>Qtd. Colaboradores</TableHead>
            <TableHead>Status</TableHead>
            <TableHead className="text-center">Ações</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {(dataApi?.data || []).map((item) => (
            <TableRow key={item?.id}>
              <TableCell>{item?.unity?.name || "Todas as unidades"}</TableCell>
              <TableCell>{item?.employee?.user?.name || "Todos os colaboradores"}</TableCell>
              <TableCell>{formatDate(item?.date_start)}</TableCell>
              <TableCell>{formatDate(item?.date_end)}</TableCell>
              <TableCell>
                {item?.processed_employees}/{item?.total_employees}
              </TableCell>
              <TableCell>
                <span
                  className={cn(
                    "px-2 py-1 rounded-full text-xs font-medium",
                    getStatusColor(item?.status)
                  )}
                >
                  {STATUS_LABELS[item?.status]}
                </span>
              </TableCell>
              <TableCell className="flex justify-center">
                <div className="flex gap-1 w-full justify-center">
                  <If condition={hasPermission("show-time-entry-mirror-details")}>
                    <ShowBtn uuid={item?.uuid} />
                  </If>
                  <If condition={hasPermission("reprocess-time-entry-mirror")}>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="size-7 p-0 hover:bg-stone-950/10"
                      title="Reprocessar"
                      disabled={reprocessingUuid === item?.uuid}
                      onClick={() => handleReprocess(item?.uuid)}
                    >
                      <RotateCcw
                        className={cn("size-5 text-black", reprocessingUuid === item?.uuid && "animate-spin")}
                      />
                    </Button>
                  </If>
                  <If condition={hasPermission("delete-time-entry-mirror")}>
                    <DeleteBtn
                      params={{
                        func: deleteTimeEntryMirror,
                        funcParams: item?.uuid,
                      }}
                      updateFront={() =>
                        updateFront((prev) => ({
                          ...prev,
                          data: [...prev?.data?.filter((data) => data?.uuid !== item?.uuid)],
                        }))
                      }
                      referenceLabel="este relatório"
                    />
                  </If>
                </div>
              </TableCell>
            </TableRow>
          ))}
          {(dataApi?.data || []).length < 1 && (
            <TableRow>
              <TableCell colSpan={7}>Nada foi encontrado.</TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
      <div className="flex justify-between mb-3">
        {dataApi?.data?.length > 0 && (
          <span className="font-medium">
            Exibindo {dataApi?.current_page} de um total de {dataApi?.last_page}
          </span>
        )}
        {dataApi?.last_page > 1 && (
          <Pagination>
            <PaginationContent>
              <PaginationItem>
                <PaginationPrevious
                  href="#"
                  className={cn("h-8", colors.bg_primary)}
                  onClick={pageActions.previous}
                />
              </PaginationItem>
              <PaginationItem>
                <PaginationLink
                  href="#"
                  className={cn(
                    `size-8 bg-transparent border-2 border-[#023859] text-slate-900 font-bold cursor-auto hover:bg-transparent`
                  )}
                >
                  <span className="pointer-events-none dark:text-[#F4F4F5]">
                    {dataApi?.current_page}
                  </span>
                </PaginationLink>
              </PaginationItem>
              <PaginationItem>
                <PaginationLink
                  href="#"
                  className="text-black h-8 hover:bg-stone-950/10"
                  onClick={pageActions.lastPage}
                >
                  <MoreHorizontal className="h-4 w-4 dark:text-[#F4F4F5]" />
                </PaginationLink>
              </PaginationItem>
              <PaginationItem>
                <PaginationNext
                  href="#"
                  className={cn("h-8", colors.bg_primary)}
                  onClick={pageActions.next}
                />
              </PaginationItem>
            </PaginationContent>
          </Pagination>
        )}
      </div>
    </>
  );
};
